import { handleExportCSV } from '@/global/components/DataGrid/components/CustomGridToolBar/ExportarCsv'
import { colunasExportaveis } from '@/global/components/DataGrid/components/CustomGridToolBar/csv-format'
import { aplicarVisibilidade } from '@/global/components/DataGrid/utils/columnVisibilityStorage'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import { RadioGroup, RadioGroupItem } from '@/global/components/RadioGroup'
import { TextField } from '@/global/components/TextField'
import Grid from '@/global/components/ui/Grid'
import { FormFieldSelect } from 'global/components/Form'
import IconeAjuda from 'global/components/IconeAjuda'
import { SelectEmpresa, SelectFuncionario } from 'global/components/Selects'
import Dialog from 'global/guides/Dialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { useTransmit } from 'hooks/useTransmit'
import { useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { funcionario as funcionarioApi } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import {
    buscarFuncionariosFiltrados,
    FiltrosExportacao,
    ExportarService as useService,
} from '../../service/exportar'

type Formato = 'pdf' | 'xls' | 'csv'

type ExportarMessage =
    | { status: 'PROCESSANDO'; progresso: number }
    | { status: 'CONCLUIDO'; filename: string }
    | { status: 'ERRO'; message: string }

const ExportarFuncionario = () => {
    const navigate = useNavigate()
    const toggle = () => navigate('/cadastros/funcionarios')
    const funcionarioId = useStore(state => state.employeeId)
    const empresaId = useStore(state => state.companyId)
    const { filtrosExportacao, columns } = useOutletContext<{
        filtrosExportacao: FiltrosExportacao
        columns: GridColDefPro[]
    }>()
    const visibilidadeColunas = useStore(
        state => state.dataTable.colunas.funcionarios,
    )
    const { handleExportarFicha, isCarregando } = useService()
    const [exportarTodos, setExportarTodos] = useState(true)
    const [formato, setFormato] = useState<Formato>('xls')
    const [progresso, setProgresso] = useState<number | null>(null)

    const subscribe = useTransmit<ExportarMessage>({
        onMessage: async (message, unsubscribe) => {
            if (message.status === 'PROCESSANDO') {
                setProgresso(message.progresso)
                return
            }

            if (message.status === 'CONCLUIDO') {
                try {
                    await funcionarioApi.exportar.file(message.filename)
                } catch (err) {
                    toast.error('Erro ao baixar o arquivo. Tente novamente.')
                    console.error('[Exportar] falha no download:', err)
                }
                await unsubscribe()
                setProgresso(null)
                return
            }

            if (message.status === 'ERRO') {
                toast.error(message.message)
                await unsubscribe()
                setProgresso(null)
            }
        },
    })

    /** Gera o CSV como as demais telas: colunas visíveis do grid e valores formatados. */
    const handleExportarCsv = async (filtros: FiltrosExportacao) => {
        setProgresso(0)
        try {
            const funcionarios = await buscarFuncionariosFiltrados(
                empresaId,
                filtros,
            )
            await handleExportCSV({
                rows: funcionarios,
                columns: colunasExportaveis(
                    aplicarVisibilidade(columns, visibilidadeColunas ?? {}),
                ),
                fileName: 'exportacao-funcionarios',
            })
        } catch (error) {
            toast.error('Erro ao exportar funcionários. Tente novamente.')
            console.error('[Exportar] falha ao gerar csv:', error)
        } finally {
            setProgresso(null)
        }
    }

    const handleExportarTodos = async () => {
        const cod = window.crypto.randomUUID()
        let unsubscribe: (() => void | Promise<void>) | undefined

        setProgresso(0)
        try {
            unsubscribe = await subscribe(cod)

            const response = await funcionarioApi.exportar.post<
                FiltrosExportacao & {
                    empresaId: number
                    formato: Formato
                    cod: string
                },
                { cod: string }
            >({ empresaId, formato, cod, ...filtrosExportacao })

            if (response.cod !== cod) {
                throw new Error(
                    'Backend retornou um canal diferente do canal assinado.',
                )
            }
        } catch (error) {
            if (unsubscribe) {
                await unsubscribe()
            }

            toast.error('Erro ao iniciar exportação. Tente novamente.')
            console.error('[Exportar] falha ao iniciar exportação:', error)
            setProgresso(null)
        }
    }

    const isProcessando = progresso !== null

    return (
        <Dialog.Root
            loading={isCarregando || isProcessando}
            onClose={toggle}
            onSubmit={() => {
                if (formato === 'csv') {
                    handleExportarCsv(
                        exportarTodos
                            ? filtrosExportacao
                            : {
                                  filter: JSON.stringify([
                                      { c: 'id', o: '=', v: funcionarioId },
                                  ]),
                              },
                    )
                } else if (exportarTodos) {
                    handleExportarTodos()
                } else {
                    handleExportarFicha(funcionarioId, formato)
                }
            }}
            open={hasPermission(['read', 'ExportarFuncionários'])}
            width="3xl"
        >
            <Dialog.Title>
                Exportar funcionário
                <IconeAjuda>
                    Os dados incluídos no arquivo são:
                    <ul className="pl-4">
                        {[
                            'Matrícula',
                            'Nome',
                            'Cargo/Função',
                            'PIS',
                            'CPF',
                            'Data de admissão',
                        ].map((item, index) => (
                            <li key={index}>{item}</li>
                        ))}
                    </ul>
                    <br />
                    Ao exportar todos, saem os funcionários listados na tela,
                    com os mesmos filtros e busca aplicados.
                    <br />
                    No entanto, é possível exportar apenas a ficha do
                    funcionário, que contém todos os dados cadastrados.
                </IconeAjuda>
            </Dialog.Title>
            <Dialog.Content>
                <Grid container>
                    <Grid item xs={12} sm={6}>
                        <SelectEmpresa variant="form" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <RadioGroup
                            aria-label="exportar"
                            defaultValue="todos"
                            name="exportar"
                            onValueChange={valor => {
                                setExportarTodos(valor === 'todos')
                            }}
                            className="flex flex-row justify-center mt-4.5 mb-6"
                        >
                            <RadioGroupItem
                                value="todos"
                                label="Exportar todos"
                            />
                            <RadioGroupItem
                                value="ficha"
                                label="Exportar ficha"
                            />
                        </RadioGroup>
                    </Grid>
                    <Grid item xs={12}>
                        {!exportarTodos ? (
                            <SelectFuncionario variant="form" />
                        ) : (
                            <TextField
                                placeholder="Todos"
                                label="Funcionário"
                                disabled
                            />
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <FormFieldSelect
                            required
                            label="Formato de exportação"
                            name="formato"
                            defaultValue={'xls'}
                            options={[
                                {
                                    value: 'xls',
                                    label: 'Planilha (XLS) - padrão de importação',
                                },
                                {
                                    value: 'pdf',
                                    label: 'Documento (PDF) - ficha dos funcionários',
                                },
                                {
                                    value: 'csv',
                                    label: 'Planilha (CSV) - colunas da tela',
                                },
                            ]}
                            onChange={valor => {
                                setFormato(valor as Formato)
                            }}
                        />
                    </Grid>
                </Grid>
            </Dialog.Content>
            <Dialog.Actions progress={progresso ?? undefined}>
                <Dialog.Actions.SecondaryButton disabled={isProcessando} />
                <Dialog.Actions.PrimaryButton disabled={isProcessando}>
                    {isProcessando ? 'Exportando...' : 'Exportar'}
                </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}

export default ExportarFuncionario
